/**
 * Callix Dialer Widget - Entry Point para CDN
 * 
 * ⚠️ SEGURANÇA:
 * - Este widget NÃO contém credenciais sensíveis (API_KEY, DOMAIN)
 * - Todas as credenciais ficam no backend (API: /api/user-sdk-session)
 * - Cliente envia apenas USERNAME → Backend valida e retorna TOKEN
 * - Token é temporário e gerenciado pelo backend
 * 
 * 📦 USO VIA CDN:
 * ```html
 * <script src="https://cdn.jsdelivr.net/npm/callix-dialer-widget@2/dist-widget/callix-dialer.umd.js"></script>
 * <script>
 *   CallixDialer.init({
 *     container: '#dialer',
 *     apiUrl: 'https://seu-backend.com/api/user-sdk-session'
 *   });
 * </script>
 * ```
 */

import { createRoot, type Root } from 'react-dom/client';
import { FloatingDialer } from '../components/FloatingDialer';
import { InlineDialer } from '../components/InlineDialer';
import { StrictMode, useState, useEffect, useRef, useCallback, useMemo, createContext, useContext, forwardRef, useImperativeHandle } from 'react';
import { CallixClientProvider, useCallOperatorControls, useCallOperator, useCallOperatorState, useCallOperatorEventListener } from '../lib/callixSdk';
import { EngineAccessError, EngineClient } from '../lib/engineClient';
import type { AgentView } from '../../engine/protocol';
import { EngineClientProvider } from '../lib/engineSdk';
import { describeSessionDrop, describeStartupError, type DialerNotice } from '../lib/dialerDiagnostics';
import {
  DialerTabBus,
  LEADER_HEARTBEAT_MS,
  anyDialerTab,
  claimLeader,
  clearTabRole,
  findLeader,
  newTabId,
  readLeader,
  readTabRole,
  releaseLeader,
  saveTabRole,
  takeoverDelayMs,
  type TabRole,
} from '../lib/dialerTabs';
import { parseDensity, type DialerDensity } from '../lib/deviceProfile';
import { ClickToCallAgent } from '../components/ClickToCallAgent';
import type { ClickToCallTaskPublic } from '../lib/clickToCallTypes';

/** Versão publicada do widget (mantenha igual à de package-widget.json). */
export const WIDGET_VERSION = '2.8.3';

// ============================================================================
// WEBSOCKET SIP CLEANUP - Fecha conexões SIP órfãs ANTES de iniciar
// Isso é CRÍTICO para resolver o erro SIP 409 em reload de página
// ============================================================================

// Armazena referência ao WebSocket SIP atual para cleanup
let currentSipWebSocket: WebSocket | null = null;

/**
 * Tenta enviar uma mensagem SIP UNREGISTER antes de fechar a conexão
 * Isso pode ajudar o Kamailio a limpar o registro imediatamente
 */
function sendSipUnregisterIfPossible(ws: WebSocket): void {
  if (ws.readyState !== WebSocket.OPEN) return;
  
  try {
    // Mensagem SIP mínima de UNREGISTER (pode não funcionar dependendo do servidor)
    // O importante é tentar fechar gracefully
    console.log('[Callix Dialer] 📤 Tentando enviar sinalização de fechamento...');
    
    // A biblioteca jssip armazena informações no WebSocket
    // Vamos tentar acessar para obter o Call-ID
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wsAny = ws as Record<string, any>;
    
    // Verificar se há método de cleanup disponível
    if (wsAny._ua && typeof wsAny._ua.stop === 'function') {
      console.log('[Callix Dialer] 🔌 Encontrado UA, chamando stop()...');
      wsAny._ua.stop();
    } else if (wsAny._connection && typeof wsAny._connection.close === 'function') {
      console.log('[Callix Dialer] 🔌 Fechando conexão interna...');
      wsAny._connection.close();
    }
  } catch (err) {
    console.warn('[Callix Dialer] ⚠️ Não foi possível enviar UNREGISTER:', err);
  }
}

/**
 * Intercepta criação de WebSockets para capturar conexões SIP
 * Isso permite fechar a conexão SIP ao recarregar a página
 */
function setupWebSocketInterceptor() {
  if (typeof window === 'undefined') return;
  
  // Evitar setup duplicado
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).__callixWsInterceptorSetup) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__callixWsInterceptorSetup = true;
  
  const OriginalWebSocket = window.WebSocket;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).WebSocket = function(url: string, protocols?: string | string[]) {
    const ws = new OriginalWebSocket(url, protocols);
    
    // Detectar se é uma conexão SIP do Callix (wss://...callix.com.br/ws)
    if (typeof url === 'string' && url.includes('callix') && url.includes('/ws')) {
      console.log('[Callix Dialer] 🔌 WebSocket SIP detectado:', url);
      currentSipWebSocket = ws;
      
      // Capturar mensagens SIP para debug
      ws.addEventListener('message', (event) => {
        if (typeof event.data === 'string' && event.data.includes('Call-ID:')) {
          const match = event.data.match(/Call-ID:\s*([^\r\n]+)/i);
          if (match) {
            // Log para debug, não precisamos armazenar
            match[1].trim();
          }
        }
      });
      
      // Limpar referência quando fechar
      ws.addEventListener('close', () => {
        console.log('[Callix Dialer] 🔌 WebSocket SIP fechado');
        if (currentSipWebSocket === ws) {
          currentSipWebSocket = null;
        }
      });
    }
    
    return ws;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;
  
  // Preservar propriedades estáticas do WebSocket
  Object.assign(window.WebSocket, {
    CONNECTING: OriginalWebSocket.CONNECTING,
    OPEN: OriginalWebSocket.OPEN,
    CLOSING: OriginalWebSocket.CLOSING,
    CLOSED: OriginalWebSocket.CLOSED,
  });
  
  console.log('[Callix Dialer] ✅ WebSocket interceptor configurado');
}

/**
 * Fecha qualquer WebSocket SIP ativo
 * Deve ser chamado ANTES de iniciar nova autenticação
 */
function closeExistingSipWebSocket(): Promise<void> {
  return new Promise((resolve) => {
    if (!currentSipWebSocket) {
      console.log('[Callix Dialer] 📭 Nenhum WebSocket SIP ativo para fechar');
      resolve();
      return;
    }
    
    if (currentSipWebSocket.readyState === WebSocket.CLOSED || 
        currentSipWebSocket.readyState === WebSocket.CLOSING) {
      console.log('[Callix Dialer] 📭 WebSocket SIP já está fechando/fechado');
      currentSipWebSocket = null;
      resolve();
      return;
    }
    
    console.log('[Callix Dialer] 🔌 Fechando WebSocket SIP existente...');
    
    const ws = currentSipWebSocket;
    
    // Tentar enviar sinalização de cleanup antes de fechar
    sendSipUnregisterIfPossible(ws);
    
    const timeoutId = setTimeout(() => {
      console.log('[Callix Dialer] ⏱️ Timeout ao fechar WebSocket, continuando...');
      // Forçar fechamento
      try {
        ws.close(1000, 'Force close after timeout');
      } catch {
        // Ignorar erro
      }
      resolve();
    }, 3000); // Aumentar timeout para dar tempo ao UNREGISTER
    
    ws.addEventListener('close', () => {
      clearTimeout(timeoutId);
      console.log('[Callix Dialer] ✅ WebSocket SIP fechado com sucesso');
      resolve();
    }, { once: true });
    
    // Aguardar um pouco para a sinalização ser enviada antes de fechar
    setTimeout(() => {
      // Enviar código 1000 (normal closure)
      ws.close(1000, 'Page reload - cleaning up SIP connection');
    }, 500);
  });
}

/**
 * Cleanup completo ao sair da página
 * Registra handler de beforeunload para fechar WebSocket SIP
 */
function setupPageUnloadCleanup() {
  if (typeof window === 'undefined') return;
  
  // Evitar setup duplicado
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).__callixUnloadSetup) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__callixUnloadSetup = true;
  
  window.addEventListener('beforeunload', () => {
    console.log('[Callix Dialer] 🚪 Página fechando, limpando WebSocket SIP...');
    if (currentSipWebSocket && currentSipWebSocket.readyState === WebSocket.OPEN) {
      // Fechar imediatamente - não há tempo para esperar
      currentSipWebSocket.close(1000, 'Page unload');
    }
  });
  
  // Também no pagehide (mais confiável em mobile)
  window.addEventListener('pagehide', () => {
    if (currentSipWebSocket && currentSipWebSocket.readyState === WebSocket.OPEN) {
      currentSipWebSocket.close(1000, 'Page hide');
    }
  });
  
  console.log('[Callix Dialer] ✅ Page unload cleanup configurado');
}

// Inicializar interceptors imediatamente
setupWebSocketInterceptor();
setupPageUnloadCleanup();

/**
 * Adiciona meta tag para desabilitar detecção automática de formato no iOS Safari
 * Previne que números/textos sejam detectados como telefone, endereço, etc.
 */
function setupIOSFormatDetection() {
  if (typeof document === 'undefined') return;
  
  // Verificar se já existe a meta tag
  const existingMeta = document.querySelector('meta[name="format-detection"]');
  if (existingMeta) return;
  
  // Criar e adicionar a meta tag
  const meta = document.createElement('meta');
  meta.name = 'format-detection';
  meta.content = 'telephone=no, date=no, email=no, address=no';
  document.head.appendChild(meta);
  
  console.log('[Callix Dialer] ✅ iOS format-detection meta tag adicionada');
}

// Configurar meta tag para iOS
setupIOSFormatDetection();

// ============================================================================
// CONTEXT E COMPONENTES
// ============================================================================

// Context para compartilhar skipManualQualification
type DialerConfigContextValue = {
  skipManualQualification: boolean;
};

const DialerConfigContext = createContext<DialerConfigContextValue | null>(null);

export function useDialerConfig(): DialerConfigContextValue {
  const context = useContext(DialerConfigContext);
  if (!context) {
    // Valor padrão se usado fora do provider
    return { skipManualQualification: false };
  }
  return context;
}

// ============================================================================
// SDK CLEANUP MANAGER
// Este componente é CRÍTICO para resolver o erro SIP 409
// Ele permite chamar goOffline() e stop() ANTES do CallixClientProvider desmontar
// ============================================================================

interface SdkCleanupHandle {
  /** Chama goOffline() no CallOperator para enviar SIP UNREGISTER */
  goOffline: () => void;
  /** Chama stop() no CallOperator para parar completamente */
  stop: () => void;
}

/**
 * Componente interno que expõe métodos de cleanup do SDK via ref
 * Deve ser renderizado DENTRO do CallixClientProvider
 */
interface SdkCleanupProps {
  children: React.ReactNode;
  /** o Callix derrubou a sessão desta aba (login em outro lugar, supervisor, limites...) */
  onSessionDropped?: (originator: string | null, notice: DialerNotice) => void;
}

const SdkCleanupManager = forwardRef<SdkCleanupHandle, SdkCleanupProps>(
  function SdkCleanupManager({ children, onSessionDropped }, ref) {
    const operator = useCallOperator();
    const controls = useCallOperatorControls();
    const operatorState = useCallOperatorState();
    const droppedRef = useRef(onSessionDropped);
    droppedRef.current = onSessionDropped;

    useCallOperatorEventListener('userSessionDropped', (data) => {
      const originator = (data as { originator?: string } | undefined)?.originator ?? null;
      droppedRef.current?.(originator, describeSessionDrop(originator));
    });

    // partida recusada porque o usuário já está conectado em outro lugar: mesma consequência
    const startupError = operatorState?.state === 'startupError' ? (operatorState as { error?: string }).error ?? null : null;
    useEffect(() => {
      if (startupError !== 'userIsAlreadyConnectedOnAnotherDevice') return;
      droppedRef.current?.('anotherDevice', describeStartupError(startupError));
    }, [startupError]);

    // Expor métodos de cleanup via ref
    useImperativeHandle(ref, () => ({
      goOffline: () => {
        console.log('[Callix Dialer] 📴 Chamando goOffline() para enviar SIP UNREGISTER...');
        try {
          controls.goOffline();
          console.log('[Callix Dialer] ✅ goOffline() chamado com sucesso');
        } catch (err) {
          console.error('[Callix Dialer] ❌ Erro ao chamar goOffline():', err);
        }
      },
      stop: () => {
        console.log('[Callix Dialer] 🛑 Chamando stop() no CallOperator...');
        try {
          operator.stop();
          console.log('[Callix Dialer] ✅ stop() chamado com sucesso');
        } catch (err) {
          console.error('[Callix Dialer] ❌ Erro ao chamar stop():', err);
        }
      },
    }), [operator, controls]);

    // Cleanup automático no unmount - chamar goOffline antes de desmontar. Só no desmonte de
    // verdade: `controls` é um objeto novo a cada render, então como dependência fazia o
    // goOffline() rodar a cada re-render (e, com o estado do operador assinado aqui, em laço).
    const controlsRef = useRef(controls);
    controlsRef.current = controls;
    useEffect(() => {
      return () => {
        console.log('[Callix Dialer] 🔌 SdkCleanupManager desmontando, chamando goOffline()...');
        try {
          controlsRef.current.goOffline();
        } catch (err) {
          console.warn('[Callix Dialer] ⚠️ Erro no cleanup automático:', err);
        }
      };
    }, []);

    return <>{children}</>;
  }
);

// Tipos para as opções de inicialização
type DerivedOptions = Partial<Pick<CallixDialerOptions, 'uiVariant' | 'presetUsername' | 'autoLogin' | 'density' | 'clickToCall' | 'engineUrl' | 'engineToken'>>;

function deriveOptionsFromElement(element: HTMLElement): DerivedOptions {
  const result: DerivedOptions = {};
  // data-callix-engine-url / data-callix-engine-token: modo motor
  const datasetEngineUrl = element.getAttribute('data-callix-engine-url') ?? element.dataset.callixEngineUrl;
  if (datasetEngineUrl && datasetEngineUrl.trim()) result.engineUrl = datasetEngineUrl.trim();
  const datasetEngineToken = element.getAttribute('data-callix-engine-token') ?? element.dataset.callixEngineToken;
  if (datasetEngineToken && datasetEngineToken.trim()) result.engineToken = datasetEngineToken.trim();
  const datasetMode = element.getAttribute('data-callix-mode') || element.dataset.callixMode || element.dataset.mode;
  const datasetUsername = element.getAttribute('data-callix-username') || element.dataset.callixUsername || element.dataset.username;
  const classList = Array.from(element.classList);
  const lowerClasses = classList.map((cls) => cls.toLowerCase());
  const hasCallixClass = lowerClasses.includes('callix');
  const normalizedMode = datasetMode ? datasetMode.toLowerCase() : undefined;

  if (normalizedMode === 'min' || normalizedMode === 'mini') {
    result.uiVariant = 'min';
  } else if (normalizedMode === 'mobile') {
    result.uiVariant = 'mobile';
  } else if (normalizedMode === 'default' || normalizedMode === 'floating' || normalizedMode === 'full' || normalizedMode === 'inline') {
    result.uiVariant = 'default';
  } else if (hasCallixClass) {
    if (lowerClasses.includes('mode-min') || lowerClasses.includes('mode-mini') || (lowerClasses.includes('mode') && lowerClasses.includes('min'))) {
      result.uiVariant = 'min';
    }
  }

  if (!result.presetUsername && datasetUsername) {
    result.presetUsername = datasetUsername.trim();
  }

  if (!result.presetUsername) {
    for (const className of classList) {
      const match = className.match(/(?:user|username|callix-user)[-_:]([A-Za-z0-9.@_-]+)/i);
      if (match) {
        result.presetUsername = match[1];
        break;
      }
    }
  }

  if (result.presetUsername) {
    result.presetUsername = result.presetUsername.trim();
  }

  // data-callix-auto-login="true" liga o login automático (padrão: pedir confirmação)
  const datasetAutoLogin =
    element.getAttribute('data-callix-auto-login') ?? element.dataset.callixAutoLogin ?? element.dataset.autoLogin;
  if (datasetAutoLogin !== undefined && datasetAutoLogin !== null) {
    result.autoLogin = ['true', '1', 'yes', ''].includes(datasetAutoLogin.trim().toLowerCase());
  }

  // data-callix-density="compact" | "comfortable" | "auto"
  const datasetDensity = parseDensity(element.getAttribute('data-callix-density') ?? element.dataset.callixDensity);
  if (datasetDensity) {
    result.density = datasetDensity;
  }

  // data-callix-click-to-call="false" desliga o click-to-call pela API
  const datasetClickToCall = element.getAttribute('data-callix-click-to-call') ?? element.dataset.callixClickToCall;
  if (datasetClickToCall !== undefined && datasetClickToCall !== null) {
    result.clickToCall = !['false', '0', 'no', 'off'].includes(datasetClickToCall.trim().toLowerCase());
  }

  return result;
}

interface CallixDialerOptions {
  container: string | HTMLElement;
  apiUrl?: string;
  /**
   * Host que o SDK usa como base de `https://<domain>/sdk/v1/*` (REST e Faye).
   * Quando omitido, vale o dominio devolvido por `apiUrl`. Aponte para um host
   * que faca proxy de `/sdk/v1/*` para o Callix quando a pagina nao estiver no
   * dominio do Callix - ver src/app/sdk/v1/[...path]/route.ts.
   */
  domain?: string;
  /**
   * Modo motor: URL do host do motor (engine/, ex.: https://motor.exemplo.com). Com
   * ela o widget deixa de rodar o SDK no navegador: a sessão e a mídia do Callix
   * vivem no servidor e o navegador só traz microfone e fone (WebRTC com o motor).
   * Fechar a aba não derruba a sessão. Também aceita data-callix-engine-url. Basta a URL:
   * a credencial da aba é a própria sessão do Callix que o widget cria em `apiUrl`.
   */
  engineUrl?: string;
  /**
   * Token compartilhado do host (ENGINE_TAB_TOKEN); data-callix-engine-token. Desde a 2.8.0 o
   * widget não precisa dele: só serve para uso administrativo (página tab/ do motor, roteiros).
   * Não o coloque em página pública.
   */
  engineToken?: string;
  mode?: 'floating' | 'inline'; // Novo: modo de exibição
  theme?: 'light' | 'dark';
  position?: 'bottom-right' | 'bottom-left';
  onReady?: () => void;
  onError?: (error: Error) => void;
  uiVariant?: 'default' | 'min' | 'mobile';
  presetUsername?: string;
  /**
   * @deprecated Desde a 2.7.0 não tem efeito: o widget nunca cria uma sessão no Callix sem a
   * confirmação do agente (abrir ou atualizar uma aba não pode derrubar o Callix web). Ele
   * entra sozinho apenas quando já existe uma conexão ativa deste usuário: outra aba deste
   * navegador (modo SDK) ou o motor segurando a sessão (modo motor). A decisão de entrar ou
   * sair vale para todas as abas abertas.
   */
  autoLogin?: boolean;
  /**
   * Densidade da interface do modo mobile. 'auto' (padrão) usa o tipo de ponteiro:
   * mouse/hover = 'compact' (alvos menores, mais conteúdo), toque = 'comfortable'.
   * Nunca depende da largura da janela/resolução. Também aceita data-callix-density.
   */
  density?: DialerDensity;
  /**
   * Click-to-call pela API do backend (POST /api/click-to-call). O widget consulta
   * o backend a cada poucos segundos e, ao receber uma tarefa para o usuário logado
   * (disponível ou em pausa), dispara a ligação manual e vai para a tela de chamada.
   * Padrão: ligado. `false` desliga; um objeto ajusta o intervalo e recebe um
   * callback. Também aceita data-callix-click-to-call="false" no container.
   */
  clickToCall?: boolean | ClickToCallOptions;
  portalTarget?: HTMLElement | null;
}

export interface ClickToCallOptions {
  enabled?: boolean;
  /** Intervalo base entre consultas (ms). Padrão: 4000 (o servidor pode sugerir outro). */
  pollIntervalMs?: number;
  /** Chamado quando uma tarefa chega, antes de discar. */
  onTask?: (task: ClickToCallTaskPublic) => void;
}

function normalizeClickToCallOptions(value: CallixDialerOptions['clickToCall']): Required<Pick<ClickToCallOptions, 'enabled'>> & ClickToCallOptions {
  if (value === false) {
    return { enabled: false };
  }
  if (value === true || value === undefined || value === null) {
    return { enabled: true };
  }
  return { ...value, enabled: value.enabled !== false };
}

// Classe principal do Widget (similar a uma classe Python exportável)
class CallixDialerWidget {
  private root: Root | null = null;
  private container: HTMLElement | null = null;
  private options: CallixDialerOptions;

  constructor(options: CallixDialerOptions) {
    this.options = options;
  }

  /**
   * Inicializa o widget no DOM
   * Similar a: widget = MinhaClasse(); widget.init()
   */
  init(): void {
    try {
      // Encontrar ou criar container
      if (typeof this.options.container === 'string') {
        this.container = document.querySelector(this.options.container);
      } else {
        this.container = this.options.container;
      }

      if (!this.container) {
        throw new Error(`Container não encontrado: ${this.options.container}`);
      }

      const derived = deriveOptionsFromElement(this.container);
      if (!this.options.uiVariant && derived.uiVariant) {
        this.options.uiVariant = derived.uiVariant;
      }
      if (!this.options.presetUsername && derived.presetUsername) {
        this.options.presetUsername = derived.presetUsername;
      }
      if (this.options.autoLogin === undefined && derived.autoLogin !== undefined) {
        this.options.autoLogin = derived.autoLogin;
      }
      if (this.options.density === undefined && derived.density !== undefined) {
        this.options.density = derived.density;
      }
      if (this.options.clickToCall === undefined && derived.clickToCall !== undefined) {
        this.options.clickToCall = derived.clickToCall;
      }
      if (!this.options.engineUrl && derived.engineUrl) {
        this.options.engineUrl = derived.engineUrl;
      }
      if (!this.options.engineToken && derived.engineToken) {
        this.options.engineToken = derived.engineToken;
      }

      // Criar root do React
      this.root = createRoot(this.container);

      // Renderizar componente
      this.root.render(
        <StrictMode>
          <DialerWrapper options={this.options} containerEl={this.container} />
        </StrictMode>
      );

      // Callback de sucesso
      if (this.options.onReady) {
        this.options.onReady();
      }
    } catch (error) {
      if (this.options.onError) {
        this.options.onError(error as Error);
      } else {
        console.error('[Callix Dialer] Erro ao inicializar:', error);
      }
    }
  }

  /**
   * Destrói o widget e limpa recursos
   */
  destroy(): void {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

// Chaves para localStorage - DINÂMICAS POR USUÁRIO
const getStorageKeys = (username: string) => ({
  SESSION_TOKEN: `callix_dialer_${username}_session_token`,
  DOMAIN: `callix_dialer_${username}_domain`,
  TIMESTAMP: `callix_dialer_${username}_timestamp`,
  SKIP_MANUAL_QUALIFICATION: `callix_dialer_${username}_skip_manual_qualification`,
});

// Chave global para último usuário logado
const LAST_USERNAME_KEY = 'callix_dialer_last_username';

/** opções do logout: `broadcast` avisa as outras abas; `keepDecision` só solta a conexão (outra aba assume) */
type LogoutOptions = { broadcast?: boolean; keepDecision?: boolean };

function readStoredToken(user: string): string | null {
  try {
    return localStorage.getItem(getStorageKeys(user).SESSION_TOKEN);
  } catch {
    return null;
  }
}

/** configuração do serviço: chamadas manuais pulam a qualificação? (null = não deu para saber) */
async function fetchSkipQualification(domain: string, token: string): Promise<boolean | null> {
  try {
    const response = await fetch(`https://${domain}/sdk/v1/client/user-service/config`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data?.service?.outboundOptions?.skipQualification === true;
  } catch {
    return null;
  }
}

// Componente wrapper interno
function DialerWrapper({ options, containerEl }: { options: CallixDialerOptions; containerEl?: HTMLElement | null }) {
  const [username, setUsername] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [callixDomain, setCallixDomain] = useState<string | null>(null);
  const [isRestoringSession, setIsRestoringSession] = useState(true);
  const [skipManualQualification, setSkipManualQualification] = useState<boolean>(false);
  // Etapa atual do login, exibida na tela de login enquanto isLoading
  const [loginStatus, setLoginStatus] = useState<string | null>(null);
  
  // Ref para o SdkCleanupManager - permite chamar goOffline/stop antes do unmount
  const cleanupRef = useRef<SdkCleanupHandle>(null);
  // Key única para forçar recriação do CallixClientProvider
  // Isso garante que a conexão SIP seja completamente destruída e recriada
  const [providerKey, setProviderKey] = useState<number>(() => Date.now());
  
  // Flag para controlar quando o SDK está "desconectando"
  // Isso cria um delay entre desmontar o provider e remontar
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const presetUsername = options.presetUsername ? options.presetUsername.trim() : null;
  const uiVariant = options.uiVariant ?? 'default';
  const portalTarget = useMemo(() => options.portalTarget ?? containerEl ?? null, [options.portalTarget, containerEl]);

  // Coordenação entre abas. O Callix aceita UMA conexão por usuário, então a decisão de usar
  // o discador por aqui (e não o Callix web) é do agente e vale para todas as abas abertas:
  // - aba nova: se já existe conexão ativa, entra sem perguntar; senão mostra "Entrar" e espera;
  // - "Entrar" numa aba entra em todas; "Sair" numa aba sai de todas;
  // - abrir ou atualizar uma aba NUNCA cria sessão sozinho (isso derrubaria o Callix web).
  // No modo SDK só uma aba segura a conexão (líder); as outras são membros. No modo motor a
  // sessão vive no servidor e todas as abas são iguais.
  const tabIdRef = useRef<string>('');
  if (!tabIdRef.current) tabIdRef.current = newTabId();
  const [tabRole, setTabRole] = useState<TabRole | null>(null);
  const tabRoleRef = useRef<TabRole | null>(null);
  tabRoleRef.current = tabRole;
  /** já sondou as outras abas (sabe se mostra "Entrar" ou "ativo em outra aba") */
  const [tabDecided, setTabDecided] = useState(false);
  /** aviso neutro na tela de login ("a sessão foi encerrada em outra aba") */
  const [tabNote, setTabNote] = useState<string | null>(null);
  /** modo motor: ainda esperando a primeira view do host para saber se há sessão */
  const [engineWaiting, setEngineWaiting] = useState(false);
  const busRef = useRef<DialerTabBus | null>(null);
  const takeoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAuthenticatedRef = useRef(false);
  isAuthenticatedRef.current = isAuthenticated;
  const isLoadingRef = useRef(false);
  isLoadingRef.current = isLoading;
  /** modo motor: logoff pedido por esta aba; não voltar a entrar enquanto o motor desliga */
  const logoffPendingRef = useRef(false);
  const handleLoginRef = useRef<(user: string) => Promise<void>>(async () => undefined);
  const handleLogoutRef = useRef<(opts?: LogoutOptions) => Promise<void>>(async () => undefined);
  const cancelTakeover = useCallback(() => {
    if (takeoverTimerRef.current) {
      clearTimeout(takeoverTimerRef.current);
      takeoverTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (options.autoLogin) {
      console.info('[Callix Dialer] autoLogin: desde a 2.7.0 o widget não cria sessão sem confirmação; ele entra sozinho só quando já existe uma conexão ativa deste usuário (outra aba ou o motor).');
    }
  }, [options.autoLogin]);

  // Ref para armazenar apiUrl e token atual para uso no beforeunload
  const currentSessionRef = useRef<{ apiUrl: string; token: string | null }>({ 
    apiUrl: options.apiUrl || '/api/user-sdk-session', 
    token: null 
  });

  // Atualizar ref quando token mudar
  useEffect(() => {
    currentSessionRef.current.token = sessionToken;
  }, [sessionToken]);

  // Modo motor: a sessão e a mídia do Callix vivem no servidor (engine/); este cliente
  // só leva o microfone e traz o som. Um cliente por usuário, reaproveitado entre logins.
  const engineMode = Boolean(options.engineUrl);
  const engineModeRef = useRef(engineMode);
  engineModeRef.current = engineMode;
  const engineRef = useRef<EngineClient | null>(null);
  const ensureEngine = useCallback(
    (user: string): EngineClient => {
      const current = engineRef.current;
      if (current && current.agentId === user) {
        current.connect();
        return current;
      }
      current?.close();
      const client = new EngineClient({
        url: options.engineUrl ?? '',
        token: options.engineToken || undefined,
        version: WIDGET_VERSION,
        // credencial da aba: o token da sessão do Callix guardado neste navegador (lido a cada
        // hello, para a reconexão depois de uma troca de sessão pegar o token novo)
        sessionToken: () => readStoredToken(user),
        agentId: user,
        label: 'Discador',
        log: (message) => console.log('[Callix Dialer/motor]', message),
      });
      client.onSessionLost((lost) => {
        // o Callix derrubou a sessão (login em outro lugar, supervisor...): volta à tela de
        // login com o motivo; não reentra sozinho, senão derrubaria o login novo do agente
        const notice = describeSessionDrop(lost.originator);
        setError(`${notice.title}: ${notice.detail}${notice.action ? ` ${notice.action}` : ''}`);
        setIsAuthenticated(false);
        setSessionToken(null);
        setTabNote(null);
      });
      client.connect();
      engineRef.current = client;
      return client;
    },
    [options.engineUrl, options.engineToken]
  );
  useEffect(
    () => () => {
      engineRef.current?.close();
      engineRef.current = null;
    },
    []
  );

  // Fazer logout quando a página for fechada (beforeunload)
  // Isso ajuda a liberar o registro SIP no servidor
  useEffect(() => {
    // beforeunload e pagehide (o segundo é o que vale no Safari/iOS); só uma execução por descarga
    let unloadHandled = false;
    const handleBeforeUnload = () => {
      // no modo motor fechar a aba não encerra a sessão: ela continua no servidor
      if (engineModeRef.current || unloadHandled) return;
      unloadHandled = true;
      setTimeout(() => {
        unloadHandled = false; // o bfcache pode trazer a página de volta
      }, 2_000);
      const { apiUrl, token } = currentSessionRef.current;
      if (tabRoleRef.current === 'leader') {
        // avisa os membros: depois de um prazo (um F5 volta antes) um deles assume a conexão
        busRef.current?.post({ type: 'leaving' });
        if (busRef.current) releaseLeader(busRef.current.username, tabIdRef.current);
      }
      if (token) {
        // Usar sendBeacon para garantir que a requisição seja enviada mesmo ao fechar
        const data = JSON.stringify({ logout: true });
        const logoutUrl = `${apiUrl}?logout=true`;
        
        // Tentar com sendBeacon (mais confiável ao fechar página)
        if (navigator.sendBeacon) {
          const blob = new Blob([data], { type: 'application/json' });
          navigator.sendBeacon(logoutUrl, blob);
          console.log('[Callix Dialer] Logout enviado via sendBeacon');
        } else {
          // Fallback: fetch síncrono (não recomendado, mas melhor que nada)
          fetch(apiUrl, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
            keepalive: true,
          }).catch(() => {});
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, []);

  // NÃO restaurar sessão do localStorage - sempre fazer novo login
  // Isso resolve o problema de SIP 409 (conflito de registro)
  // O servidor SIP requer uma nova sessão a cada pageload para evitar conflitos
  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsRestoringSession(false);
      return;
    }

    try {
      // Buscar o último username usado para auto-login
      const storedLastUsername = localStorage.getItem(LAST_USERNAME_KEY);

      if (!storedLastUsername) {
        console.log('[Callix Dialer] Nenhum username salvo, aguardando login manual');
        setIsRestoringSession(false);
        return;
      }

      if (presetUsername && storedLastUsername !== presetUsername) {
        console.log('[Callix Dialer] Ignorando sessao salva por mismatch com preset:', storedLastUsername, '!=', presetUsername);
        setIsRestoringSession(false);
        return;
      }

      // Apenas restaurar o username para facilitar auto-login
      // MAS NÃO restaurar o token - vamos buscar um novo do backend
      console.log('[Callix Dialer] Username encontrado, irá fazer novo login:', storedLastUsername);
      setUsername(storedLastUsername);
      
      // Restaurar apenas skipManualQualification (configuração local)
      const STORAGE_KEYS = getStorageKeys(storedLastUsername);
      const storedSkipQualification = localStorage.getItem(STORAGE_KEYS.SKIP_MANUAL_QUALIFICATION);
      if (storedSkipQualification) {
        setSkipManualQualification(storedSkipQualification === 'true');
      }

      // Não definir isAuthenticated = true
      // O auto-login será feito pelo useEffect de presetUsername
    } catch (err) {
      console.error('[Callix Dialer] Erro ao verificar sessão:', err);
    } finally {
      setIsRestoringSession(false);
    }
  }, [presetUsername]);

  useEffect(() => {
    if (presetUsername && username !== presetUsername) {
      setUsername(presetUsername);
    }
  }, [presetUsername, username]);

  // ---------------------------------------------------------------- modo motor: entrar e sair junto com o servidor
  const engineDomain = useCallback(
    () => options.domain || new URL(options.apiUrl || '/api/user-sdk-session', window.location.href).host,
    [options.domain, options.apiUrl]
  );

  /** passa a "autenticado" com a sessão que o motor segura (o token só serve ao formulário da campanha) */
  const enterEngine = useCallback(async (user: string, domain: string, token: string | null) => {
    if (token) {
      const skip = await fetchSkipQualification(domain, token);
      if (skip !== null) {
        setSkipManualQualification(skip);
        try {
          localStorage.setItem(getStorageKeys(user).SKIP_MANUAL_QUALIFICATION, String(skip));
        } catch {
          // localStorage indisponível
        }
      }
    }
    try {
      localStorage.setItem(LAST_USERNAME_KEY, user);
      if (token) {
        const STORAGE_KEYS = getStorageKeys(user);
        localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, token);
        localStorage.setItem(STORAGE_KEYS.DOMAIN, domain);
        localStorage.setItem(STORAGE_KEYS.TIMESTAMP, Date.now().toString());
      }
    } catch {
      // localStorage indisponível
    }
    setSessionToken(token);
    setCallixDomain(domain);
    setUsername(user);
    setIsAuthenticated(true);
    setError(null);
    setTabNote(null);
    setProviderKey(Date.now());
  }, []);

  /** o motor já tem a sessão deste agente: entra sem criar sessão nova */
  const joinEngine = useCallback(
    async (user: string) => {
      setIsLoading(true);
      setError(null);
      setLoginStatus('Entrando na sessão do motor...');
      try {
        await enterEngine(user, engineDomain(), readStoredToken(user));
        console.log('[Callix Dialer] Conectado ao motor como', user);
      } catch (err) {
        console.error('[Callix Dialer] Erro ao entrar na sessão do motor:', err);
        setError(err instanceof Error ? err.message : 'Erro ao conectar ao motor');
      } finally {
        setIsLoading(false);
        setLoginStatus(null);
      }
    },
    [enterEngine, engineDomain]
  );

  // O motor é a fonte da verdade: se ele já segura a sessão deste agente (login feito em outra
  // aba, F5, outro computador), esta aba entra sem perguntar; se ele desliga (Sair em outra
  // aba, sessão derrubada, servidor reiniciado), esta aba volta à tela de login e fica lá.
  useEffect(() => {
    if (!engineMode || !username || isRestoringSession) return;
    const user = username;
    const client = ensureEngine(user);
    setEngineWaiting(!client.getState().view);
    const fallback = setTimeout(() => setEngineWaiting(false), 15_000);
    let joining = false;
    let rejectedNote = false;
    const sync = () => {
      const { view, sessionLost, rejected } = client.getState();
      if (!rejected && rejectedNote) {
        // o host voltou a aceitar esta aba (a sessão de lá acabou): o aviso de uso em outro lugar sai
        rejectedNote = false;
        setTabNote(null);
      }
      if (rejected) {
        rejectedNote = true;
        // o motor roda a sessão deste agente com outra credencial (outro navegador ou computador):
        // esta aba só entra com um login novo, que substitui a sessão de lá
        setEngineWaiting(false);
        if (isLoadingRef.current) return;
        if (isAuthenticatedRef.current) {
          console.log('[Callix Dialer] o motor recusou esta aba: a sessão do agente foi trocada por um login em outro lugar');
          setIsAuthenticated(false);
          setSessionToken(null);
          setCallixDomain(null);
        }
        setTabNote(rejected.message);
        return;
      }
      if (!view) return;
      setEngineWaiting(false);
      if (view.engine === 'stopped' || view.engine === 'crashed') logoffPendingRef.current = false;
      const hasSession = (view.engine === 'running' || view.engine === 'starting') && !sessionLost;
      if (hasSession && !logoffPendingRef.current && !isAuthenticatedRef.current && !isLoadingRef.current && !joining) {
        joining = true;
        console.log('[Callix Dialer] o motor já segura a sessão deste agente; entrando por aqui também');
        void joinEngine(user).finally(() => {
          joining = false;
        });
      } else if (view.engine === 'stopped' && isAuthenticatedRef.current && !isLoadingRef.current) {
        console.log('[Callix Dialer] o motor desligou (Sair em outra aba ou servidor reiniciado); voltando à tela de login');
        setIsAuthenticated(false);
        setSessionToken(null);
        setCallixDomain(null);
        setTabNote('O discador foi encerrado em outra aba ou o motor foi reiniciado.');
      }
    };
    sync();
    const off = client.subscribe(sync);
    return () => {
      off();
      clearTimeout(fallback);
    };
  }, [engineMode, username, isRestoringSession, ensureEngine, joinEngine]);

  // ---------------------------------------------------------------- modo SDK: uma aba líder, as outras membros
  const scheduleTakeoverRef = useRef<(() => void) | null>(null);

  // canal com as outras abas deste usuário (recriado quando o usuário muda)
  useEffect(() => {
    if (engineMode || !username) {
      setTabDecided(true);
      return;
    }
    // usuário novo (restaurado do localStorage ou digitado): sonda as outras abas de novo
    setTabDecided(false);
    const user = username;
    const bus = new DialerTabBus({
      username: user,
      tabId: tabIdRef.current,
      status: () => ({ role: tabRoleRef.current, authenticated: isAuthenticatedRef.current }),
    });
    busRef.current = bus;
    const becomeMember = () => {
      if (isAuthenticatedRef.current || tabRoleRef.current === 'leader') return;
      setTabRole('member');
      saveTabRole(user, 'member');
      setTabNote(null);
      setError(null);
      setTabDecided(true);
    };
    const scheduleTakeover = () => {
      cancelTakeover();
      takeoverTimerRef.current = setTimeout(() => {
        takeoverTimerRef.current = null;
        if (tabRoleRef.current !== 'member' || isAuthenticatedRef.current || isLoadingRef.current) return;
        const current = readLeader(user);
        if (current && current.tabId !== tabIdRef.current) return; // outra aba foi mais rápida
        if (!claimLeader(user, tabIdRef.current)) return;
        bus.post({ type: 'claim' });
        console.log('[Callix Dialer] a aba com o discador fechou; esta aba assume a conexão');
        void handleLoginRef.current(user);
      }, takeoverDelayMs());
    };
    scheduleTakeoverRef.current = scheduleTakeover;
    const off = bus.on((message) => {
      switch (message.type) {
        case 'login':
        case 'claim':
          cancelTakeover();
          becomeMember();
          break;
        case 'leaving':
          if (tabRoleRef.current === 'member' && !isAuthenticatedRef.current) scheduleTakeover();
          break;
        case 'takeover':
          if (tabRoleRef.current === 'leader') {
            console.log('[Callix Dialer] outra aba pediu o discador; soltando a conexão');
            void handleLogoutRef.current({ broadcast: false, keepDecision: true });
          }
          break;
        case 'logout':
          cancelTakeover();
          void handleLogoutRef.current({ broadcast: false });
          setTabNote('Você saiu do discador em outra aba.');
          break;
        case 'dropped': {
          cancelTakeover();
          releaseLeader(user, tabIdRef.current);
          setTabRole(null);
          clearTabRole();
          const notice = describeSessionDrop(message.originator);
          setError(`${notice.title}: ${notice.detail}${notice.action ? ` ${notice.action}` : ''}`);
          break;
        }
        default:
          break;
      }
    });
    return () => {
      off();
      bus.close();
      cancelTakeover();
      scheduleTakeoverRef.current = null;
      if (busRef.current === bus) busRef.current = null;
      setTabDecided(false);
    };
  }, [engineMode, username, cancelTakeover]);

  // primeira decisão desta aba: já existe conexão ativa deste usuário em outra aba?
  useEffect(() => {
    if (engineMode || !username || isAuthenticated || tabDecided || isRestoringSession || isLoading) return;
    const bus = busRef.current;
    if (!bus) return;
    const user = username;
    const stored = readTabRole(user);
    if (stored === 'leader') {
      // esta aba segurava o discador e recarregou (F5): reconecta sem perguntar de novo,
      // a não ser que um membro já tenha assumido enquanto ela voltava
      cancelTakeover();
      setTabDecided(true);
      if (claimLeader(user, tabIdRef.current)) {
        bus.post({ type: 'claim' });
        console.log('[Callix Dialer] esta aba segurava o discador antes de recarregar; reconectando');
        void handleLoginRef.current(user);
      } else {
        setTabRole('member');
        saveTabRole(user, 'member');
      }
      return;
    }
    let cancelled = false;
    void bus.probe().then((pongs) => {
      if (cancelled) return;
      setTabDecided(true);
      // a trava renovada pela líder (localStorage) vale como resposta: uma aba ocupada com o
      // SDK pode não responder ao ping a tempo
      const leaderLock = readLeader(user);
      if (findLeader(pongs) || (leaderLock && leaderLock.tabId !== tabIdRef.current) || (stored === 'member' && anyDialerTab(pongs))) {
        setTabRole('member');
        saveTabRole(user, 'member');
      } else if (stored === 'member') {
        // era membro e a líder sumiu enquanto esta aba recarregava: assume depois do prazo
        setTabRole('member');
        scheduleTakeoverRef.current?.();
      } else {
        setTabRole(null);
        clearTabRole();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [engineMode, username, isAuthenticated, tabDecided, isRestoringSession, isLoading, cancelTakeover]);

  // a líder renova a trava enquanto estiver conectada
  useEffect(() => {
    if (engineMode || tabRole !== 'leader' || !username) return;
    const user = username;
    claimLeader(user, tabIdRef.current);
    const timer = setInterval(() => claimLeader(user, tabIdRef.current), LEADER_HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [engineMode, tabRole, username]);

  // membro: ao voltar o foco, confere se a líder ainda existe. Sem líder e sem trava (aba
  // morta sem aviso, ou o aviso ainda a caminho), agenda a tomada da conexão: a decisão de
  // usar o discador continua valendo, e um F5 da líder ou outro membro mais rápido cancelam.
  useEffect(() => {
    if (engineMode || tabRole !== 'member' || !username) return;
    const user = username;
    const check = () => {
      if (document.visibilityState !== 'visible' || takeoverTimerRef.current) return;
      const bus = busRef.current;
      if (!bus) return;
      void bus.probe().then((pongs) => {
        if (tabRoleRef.current !== 'member' || isAuthenticatedRef.current || takeoverTimerRef.current) return;
        if (findLeader(pongs) || readLeader(user)) return;
        console.log('[Callix Dialer] a aba com o discador não responde; esta aba vai assumir');
        scheduleTakeoverRef.current?.();
      });
    };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, [engineMode, tabRole, username]);

  const handleLogin = useCallback(async (user: string) => {
    setIsLoading(true);
    setError(null);
    setLoginStatus('Preparando login...');

    console.log('[Callix Dialer] 🚀 handleLogin iniciado para usuário:', user);

    if (options.engineUrl) {
      // Modo motor: conecta ao host; se o motor já segura a sessão deste agente (F5,
      // outra aba), entra sem novo login. Senão, cria a sessão no back-end e entrega ao
      // motor, que sobe o SDK no servidor. Nunca cria sessão por cima de uma viva: o
      // login exclusivo do Callix derrubaria a que o motor está usando.
      const engineApiUrl = options.apiUrl || '/api/user-sdk-session';
      try {
        setLoginStatus('Conectando ao motor...');
        logoffPendingRef.current = false;
        const client = ensureEngine(user);
        let view: AgentView | null = null;
        try {
          view = await client.waitForView(15_000);
        } catch (err) {
          // recusada: o motor roda a sessão deste agente com outra credencial (outro navegador
          // ou computador). O agente clicou em Entrar ciente disso: login novo substitui a de lá.
          if (!(err instanceof EngineAccessError)) throw err;
          console.log('[Callix Dialer] o motor recusou a credencial desta aba; fazendo login novo que substitui a sessão de lá');
        }
        const domain = engineDomain();
        const engineHasSession = view !== null && (view.engine === 'running' || view.engine === 'starting') && !view.sessionLost;
        let token: string | null = null;
        if (engineHasSession) {
          token = readStoredToken(user);
          console.log('[Callix Dialer] motor já tem a sessão do agente; entrando sem novo login');
        } else {
          setLoginStatus('Autenticando no Callix...');
          const response = await fetch(engineApiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: user }),
            credentials: 'same-origin',
          });
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
            throw new Error(errorData.error || `Falha na autenticação: ${response.status}`);
          }
          const data = await response.json();
          token = data.userSessionToken as string;
          // guarda o token antes de entregar: as outras abas deste navegador, que o host fecha
          // na troca de sessão, reconectam apresentando este token e entram junto
          try {
            const STORAGE_KEYS = getStorageKeys(user);
            localStorage.setItem(LAST_USERNAME_KEY, user);
            localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, token);
            localStorage.setItem(STORAGE_KEYS.DOMAIN, domain);
            localStorage.setItem(STORAGE_KEYS.TIMESTAMP, Date.now().toString());
          } catch {
            // localStorage indisponível
          }
          client.setSession({ domain, userSessionToken: token });
          setLoginStatus('Subindo o motor...');
          // espera o host aceitar a sessão (ele a confirma no Callix) e subir o motor
          const up = await client.waitForView(20_000, (v) => v.engine === 'running' || v.engine === 'starting' || Boolean(v.sessionLost), { fresh: true });
          if (up.sessionLost) throw new Error(up.sessionLost.message);
        }
        await enterEngine(user, domain, token);
        console.log('[Callix Dialer] Conectado ao motor como', user);
      } catch (err) {
        console.error('[Callix Dialer] Erro ao entrar pelo motor:', err);
        setError(err instanceof Error ? err.message : 'Erro ao conectar ao motor');
        setIsAuthenticated(false);
        setSessionToken(null);
      } finally {
        setIsLoading(false);
        setLoginStatus(null);
      }
      return;
    }

    // Modo SDK: só uma aba pode segurar a conexão. Assume a liderança antes de criar a sessão;
    // se outra aba já é a líder, esta vira membro (o discador continua lá).
    if (!claimLeader(user, tabIdRef.current)) {
      console.log('[Callix Dialer] outra aba já segura a conexão deste usuário; esta aba vira membro');
      setTabRole('member');
      saveTabRole(user, 'member');
      setTabDecided(true);
      setUsername(user);
      setIsLoading(false);
      setLoginStatus(null);
      return;
    }
    busRef.current?.post({ type: 'claim' });
    cancelTakeover();
    // já é a líder (segura a trava) mesmo antes de a sessão existir: se fechar no meio do
    // login, avisa as outras abas e solta a trava como qualquer líder
    setTabRole('leader');
    saveTabRole(user, 'leader');

    // ==========================================
    // PASSO 1: LOGOUT NO SERVIDOR - CRÍTICO PARA SIP 409
    // ==========================================
    // O registro SIP fantasma está no servidor Kamailio, NÃO no browser
    // Precisamos chamar a API para fazer logout ANTES de criar nova sessão
    
    const apiUrl = options.apiUrl || '/api/user-sdk-session';
    
    // Tentar recuperar token anterior do localStorage
    let previousToken: string | undefined;
    if (typeof window !== 'undefined') {
      try {
        const STORAGE_KEYS = getStorageKeys(user);
        previousToken = localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN) || undefined;
        console.log('[Callix Dialer] 🔍 Token anterior no localStorage:', previousToken ? 'ENCONTRADO' : 'NÃO ENCONTRADO');
      } catch (err) {
        console.warn('[Callix Dialer] Erro ao buscar token anterior:', err);
      }
    }

    // Se existe token anterior, fazer logout NO SERVIDOR primeiro
    if (previousToken) {
      setLoginStatus('Encerrando sessão anterior...');
      console.log('[Callix Dialer] 🔌 Token anterior encontrado, fazendo logout no servidor...');
      try {
        const logoutResponse = await fetch(apiUrl, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${previousToken}`,
            'x-session-token': previousToken,
          },
          credentials: 'same-origin',
        });
        console.log('[Callix Dialer] 📡 Resposta do logout:', logoutResponse.status);
        
        if (logoutResponse.ok) {
          console.log('[Callix Dialer] ✅ Logout no servidor concluído');
        } else {
          console.log('[Callix Dialer] ⚠️ Logout retornou status:', logoutResponse.status);
        }
      } catch (logoutErr) {
        console.warn('[Callix Dialer] ⚠️ Erro no logout (pode ser sessão expirada):', logoutErr);
      }

      // AGUARDAR o Kamailio processar o unregister SIP
      // Este delay é CRÍTICO - o Kamailio precisa de tempo para limpar o registro
      setLoginStatus('Aguardando liberação da sessão anterior...');
      console.log('[Callix Dialer] ⏳ Aguardando Kamailio processar unregister (5 segundos)...');
      await new Promise(resolve => setTimeout(resolve, 5000));
      console.log('[Callix Dialer] ✅ Período de espera concluído');
    }

    // Limpar localStorage
    if (typeof window !== 'undefined') {
      try {
        const STORAGE_KEYS = getStorageKeys(user);
        localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.TIMESTAMP);
        console.log('[Callix Dialer] 🗑️ Token antigo removido do localStorage');
      } catch (err) {
        console.warn('[Callix Dialer] Erro ao limpar localStorage:', err);
      }
    }

    // PASSO 2: Fechar qualquer WebSocket SIP órfão do browser (se existir)
    setLoginStatus('Fechando conexão anterior...');
    console.log('[Callix Dialer] 🔌 Verificando WebSocket SIP local...');
    await closeExistingSipWebSocket();

    // PASSO 3: Se já está autenticado (re-login), desmontar o provider primeiro
    if (isAuthenticated || sessionToken) {
      console.log('[Callix Dialer] 🔌 Preparando para fechar conexão SIP local...');
      
      // Chamar goOffline() via ref do SdkCleanupManager
      if (cleanupRef.current) {
        console.log('[Callix Dialer] 📴 Chamando goOffline() via cleanupRef...');
        cleanupRef.current.goOffline();
        await new Promise(resolve => setTimeout(resolve, 500));
        
        console.log('[Callix Dialer] 🛑 Chamando stop() via cleanupRef...');
        cleanupRef.current.stop();
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Desmontar o provider
      console.log('[Callix Dialer] ⏳ Desmontando CallixClientProvider...');
      setIsAuthenticated(false);
      setSessionToken(null);
      setIsDisconnecting(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsDisconnecting(false);
      console.log('[Callix Dialer] ✅ SDK desmontado');
    }

    // PASSO 4: Criar nova sessão no servidor
    try {
      setLoginStatus('Autenticando no Callix...');
      console.log('[Callix Dialer] 🔐 Criando nova sessão no servidor...');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: user, previousToken }),
        credentials: 'same-origin', // Importante para cookies
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `Falha na autenticação: ${response.status}`);
      }

      const data = await response.json();

      // options.domain tem precedencia sobre o dominio devolvido pela sessao.
      // E assim que se aponta o SDK para um proxy (src/app/sdk/v1/[...path]/route.ts):
      // o Callix serve /sdk/v1/faye com Cross-Origin-Resource-Policy: same-origin,
      // o que impede o transporte JSONP do Faye a partir de qualquer outra origem.
      const resolvedDomain = options.domain || data.domain || 'villelabrasil.callix.com.br';

      // Armazenar token, domain e username
      setSessionToken(data.userSessionToken);
      setCallixDomain(resolvedDomain);
      setUsername(user);
      setIsAuthenticated(true);
      setError(null);
      
      // Gerar nova key para forçar recriação do CallixClientProvider
      // Isso garante que seja uma instância completamente nova do SDK
      setProviderKey(Date.now());
      console.log('[Callix Dialer] Nova providerKey gerada para forçar recriação do SDK');

      // esta aba é a líder: as outras abas deste usuário passam a membros já
      setTabNote(null);
      setTabDecided(true);
      busRef.current?.post({ type: 'login' });

      // Buscar configuração do serviço (skipQualification para chamadas manuais)
      try {
        const configUrl = `https://${resolvedDomain}/sdk/v1/client/user-service/config`;
        setLoginStatus('Carregando configurações...');
        console.log('[Callix Dialer] 🔍 Buscando configuração do serviço:', configUrl);

        const configResponse = await fetch(configUrl, {
          headers: {
            'Authorization': `Bearer ${data.userSessionToken}`,
          },
          credentials: 'omit',
        });

        console.log('[Callix Dialer] 📡 Resposta da config:', configResponse.status);

        if (configResponse.ok) {
          const configData = await configResponse.json();
          console.log('[Callix Dialer] 📦 Dados da config recebidos:', JSON.stringify(configData, null, 2));

          const shouldSkipQualification = configData?.service?.outboundOptions?.skipQualification === true;
          console.log('[Callix Dialer] ✅ skipQualification configurado:', shouldSkipQualification);
          setSkipManualQualification(shouldSkipQualification);

          // Salvar no localStorage
          if (typeof window !== 'undefined') {
            const STORAGE_KEYS = getStorageKeys(user);
            localStorage.setItem(STORAGE_KEYS.SKIP_MANUAL_QUALIFICATION, shouldSkipQualification.toString());
            console.log('[Callix Dialer] 💾 skipQualification salvo no localStorage');
          }
        } else {
          const errorText = await configResponse.text();
          console.warn('[Callix Dialer] ⚠️ Erro ao buscar config do serviço:', configResponse.status, errorText);
        }
      } catch (configErr) {
        console.error('[Callix Dialer] ❌ Erro ao buscar config do serviço:', configErr);
      }

      // Salvar sessão no localStorage
      if (typeof window !== 'undefined') {
        try {
          const STORAGE_KEYS = getStorageKeys(user);
          localStorage.setItem(LAST_USERNAME_KEY, user);
          localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, data.userSessionToken);
          localStorage.setItem(STORAGE_KEYS.DOMAIN, resolvedDomain);
          localStorage.setItem(STORAGE_KEYS.TIMESTAMP, Date.now().toString());
          console.log('[Callix Dialer] Sessão salva no localStorage');
        } catch (err) {
          console.warn('[Callix Dialer] Não foi possível salvar sessão:', err);
        }
      }

      setLoginStatus('Conectando ao discador...');
      console.log('[Callix Dialer] Autenticado com sucesso:', user);
    } catch (err) {
      console.error('[Callix Dialer] Erro ao autenticar:', err);
      setError(err instanceof Error ? err.message : 'Erro ao autenticar');
      setIsAuthenticated(false);
      setSessionToken(null);
      releaseLeader(user, tabIdRef.current);
      setTabRole(null);
      clearTabRole();
    } finally {
      setIsLoading(false);
      setLoginStatus(null);
    }
  }, [options.apiUrl, options.domain, options.engineUrl, isAuthenticated, sessionToken, ensureEngine, enterEngine, engineDomain, cancelTakeover]);

  useEffect(() => {
    handleLoginRef.current = handleLogin;
  }, [handleLogin]);

  /**
   * Sair. Sem opções = o agente clicou em Sair: esta aba encerra a conexão e avisa as outras,
   * que saem também. `keepDecision` = só soltar a conexão para outra aba assumir (a decisão de
   * usar o discador continua). `broadcast: false` = reação a um aviso vindo de outra aba.
   */
  const handleLogout = useCallback(async (opts?: LogoutOptions) => {
    const broadcast = opts?.broadcast !== false;
    const keepDecision = opts?.keepDecision === true;
    const currentUsername = username;
    cancelTakeover();

    // PASSO 1: Chamar goOffline() ANTES de desmontar o CallixClientProvider
    // Isso envia SIP UNREGISTER para o servidor Kamailio
    if (options.engineUrl) {
      // modo motor: o próprio motor faz o logoff completo (offline, SIP, sessão no back-end);
      // as outras abas veem o motor desligar e voltam à tela de login sozinhas
      if (isAuthenticatedRef.current) {
        logoffPendingRef.current = true;
        engineRef.current?.command({ name: 'logoff' });
      }
    } else {
      if (broadcast) busRef.current?.post({ type: 'logout' });
      if (cleanupRef.current) {
        console.log('[Callix Dialer] 📴 Logout: Chamando goOffline()...');
        cleanupRef.current.goOffline();
        await new Promise(resolve => setTimeout(resolve, 500));

        console.log('[Callix Dialer] 🛑 Logout: Chamando stop()...');
        cleanupRef.current.stop();
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      if (currentUsername) releaseLeader(currentUsername, tabIdRef.current);
    }

    setIsAuthenticated(false);
    setSessionToken(null);
    setCallixDomain(null);
    setError(null);

    if (keepDecision) {
      // soltou a conexão para outra aba: continua "usando o discador", agora como membro.
      // O token fica no localStorage para a aba que assume encerrar a sessão antiga.
      setTabRole('member');
      if (currentUsername) saveTabRole(currentUsername, 'member');
      return;
    }

    setTabRole(null);
    clearTabRole();
    setUsername(presetUsername ?? null);

    // Limpar localStorage
    if (typeof window !== 'undefined' && currentUsername) {
      try {
        const STORAGE_KEYS = getStorageKeys(currentUsername);
        localStorage.removeItem(LAST_USERNAME_KEY);
        localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.DOMAIN);
        localStorage.removeItem(STORAGE_KEYS.TIMESTAMP);
        console.log('[Callix Dialer] Sessão removida do localStorage');
      } catch (err) {
        console.warn('[Callix Dialer] Não foi possível limpar sessão:', err);
      }
    }
  }, [username, presetUsername, options.engineUrl, cancelTakeover]);

  useEffect(() => {
    handleLogoutRef.current = handleLogout;
  }, [handleLogout]);

  /** membro: traz a conexão para esta aba (a líder solta; esta cria a sessão de novo) */
  const handleTakeOver = useCallback(async () => {
    const user = username;
    const bus = busRef.current;
    if (!user || !bus) return;
    setIsLoading(true);
    setError(null);
    setLoginStatus('Trazendo o discador para esta aba...');
    bus.post({ type: 'takeover' });
    // a líder faz goOffline + stop (~1 s) e solta a trava
    await new Promise((resolve) => setTimeout(resolve, 1_500));
    const current = readLeader(user);
    if (current && current.tabId !== tabIdRef.current) {
      console.log('[Callix Dialer] a outra aba não soltou a conexão; continuando como membro');
      setIsLoading(false);
      setLoginStatus(null);
      return;
    }
    await handleLoginRef.current(user);
  }, [username]);

  const handleLogoutAll = useCallback(() => {
    void handleLogoutRef.current();
  }, []);

  /** modo SDK: o Callix derrubou a sessão desta aba; ninguém assume (o agente escolheu outro acesso) */
  const handleSdkSessionDropped = useCallback(
    (originator: string | null, notice: DialerNotice) => {
      const user = username;
      if (user) releaseLeader(user, tabIdRef.current);
      busRef.current?.post({ type: 'dropped', originator });
      setTabRole(null);
      clearTabRole();
      setError(`${notice.title}: ${notice.detail}${notice.action ? ` ${notice.action}` : ''}`);
      setIsAuthenticated(false);
      setSessionToken(null);
      setCallixDomain(null);
    },
    [username]
  );

  // ainda decidindo o que mostrar: sondando as outras abas ou esperando a primeira view do motor
  const deciding = Boolean(username) && !isAuthenticated && (engineMode ? engineWaiting : !tabDecided);

  // O usuário conhecido (HTML ou último login) só entra ao confirmar; se outra aba já segura a
  // conexão (modo SDK), a tela oferece trazer o discador para cá ou sair de todas.
  const loginConfirmation = useMemo(() => {
    if (isAuthenticated || isLoading || isRestoringSession || isDisconnecting || !username || deciding) {
      return null;
    }
    return { username, locked: Boolean(presetUsername), elsewhere: !engineMode && tabRole === 'member', note: tabNote };
  }, [isAuthenticated, isLoading, isRestoringSession, isDisconnecting, username, presetUsername, deciding, engineMode, tabRole, tabNote]);

  const handleCancelLoginConfirmation = useCallback(() => {
    setUsername(null);
    setError(null);
    // Esquece o último usuário para ele não voltar a ser proposto no próximo load/aba
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(LAST_USERNAME_KEY);
      } catch {
        // localStorage indisponível
      }
    }
  }, []);

  // Determinar qual componente usar baseado no mode OU uiVariant
  // Se uiVariant='mobile', usar InlineDialer para redirecionar ao MobileDialer
  const DialerComponent = (options.mode === 'inline' || uiVariant === 'mobile') ? InlineDialer : FloatingDialer;
  const dialerMode = options.mode; // Passa mode para o InlineDialer decidir sobre modais
  const clickToCall = normalizeClickToCallOptions(options.clickToCall);

  // Se está desconectando, mostrar loading enquanto aguarda a conexão SIP ser fechada
  if (isDisconnecting) {
    return (
      <DialerConfigContext.Provider value={{ skipManualQualification }}>
        <DialerComponent
          isAuthenticated={false}
          username={username}
          onLogin={handleLogin}
          onLogout={handleLogout}
          isLoadingSession={true}
          sessionError={null}
          sessionStatus="Encerrando sessão anterior..."
          variant={uiVariant}
          portalTarget={portalTarget}
          density={options.density}
          mode={dialerMode}
        />
      </DialerConfigContext.Provider>
    );
  }

  // Se não autenticado, mostrar tela de login (no modo motor o token pode não existir:
  // o motor já tinha a sessão e o navegador não guardou o token)
  if (!isAuthenticated || (!engineMode && (!sessionToken || !callixDomain))) {
    return (
      <DialerConfigContext.Provider value={{ skipManualQualification }}>
        <DialerComponent
          isAuthenticated={false}
          username={username}
          onLogin={handleLogin}
          onLogout={handleLogout}
          isLoadingSession={isLoading || isRestoringSession || deciding}
          sessionError={error}
          sessionStatus={loginStatus}
          loginConfirmation={loginConfirmation}
          onCancelLoginConfirmation={handleCancelLoginConfirmation}
          onTakeOverSession={handleTakeOver}
          onLogoutAllTabs={handleLogoutAll}
          variant={uiVariant}
          portalTarget={portalTarget}
          density={options.density}
          mode={dialerMode}
        />
      </DialerConfigContext.Provider>
    );
  }

  // Modo motor: as telas falam com o motor pela fachada de hooks (src/lib/callixSdk.ts)
  if (engineMode && engineRef.current) {
    return (
      <DialerConfigContext.Provider value={{ skipManualQualification }}>
        <EngineClientProvider key={providerKey} client={engineRef.current}>
          <DialerComponent
            isAuthenticated={isAuthenticated}
            username={username}
            onLogin={handleLogin}
            onLogout={handleLogout}
            isLoadingSession={isLoading}
            sessionError={error}
            sessionStatus={loginStatus}
            variant={uiVariant}
            portalTarget={portalTarget}
            density={options.density}
            mode={dialerMode}
          />
          {sessionToken && (
            <ClickToCallAgent
              enabled={clickToCall.enabled}
              pollIntervalMs={clickToCall.pollIntervalMs}
              sessionToken={sessionToken}
              onTask={clickToCall.onTask}
            />
          )}
        </EngineClientProvider>
      </DialerConfigContext.Provider>
    );
  }

  if (!sessionToken || !callixDomain) {
    // só o modo motor chega aqui sem token/domínio, e ele já foi tratado acima
    return null;
  }

  // Autenticado - renderiza com CallixClientProvider
  // A key única garante que o componente seja completamente recriado a cada login
  // O SdkCleanupManager permite chamar goOffline/stop antes do unmount
  return (
    <DialerConfigContext.Provider value={{ skipManualQualification }}>
      <CallixClientProvider
        key={providerKey}
        domain={callixDomain}
        userSessionToken={sessionToken}
      >
        <SdkCleanupManager ref={cleanupRef} onSessionDropped={handleSdkSessionDropped}>
          <DialerComponent
            isAuthenticated={isAuthenticated}
            username={username}
            onLogin={handleLogin}
            onLogout={handleLogout}
            isLoadingSession={isLoading}
            sessionError={error}
            sessionStatus={loginStatus}
            variant={uiVariant}
            portalTarget={portalTarget}
            density={options.density}
            mode={dialerMode}
          />
          {/* Click-to-call: consulta o backend e dispara a ligação pedida pela API para este usuário */}
          <ClickToCallAgent
            enabled={clickToCall.enabled}
            pollIntervalMs={clickToCall.pollIntervalMs}
            sessionToken={sessionToken}
            onTask={clickToCall.onTask}
          />
        </SdkCleanupManager>
      </CallixClientProvider>
    </DialerConfigContext.Provider>
  );
}

// Função de conveniência para inicialização rápida
// Similar a: quick_init() no Python
function init(options: CallixDialerOptions): CallixDialerWidget {
  console.log(`[Callix Dialer] 🔧 Widget v${WIDGET_VERSION} inicializando...`);
  const widget = new CallixDialerWidget(options);
  widget.init();
  return widget;
}

// Objeto principal exportado - funciona tanto como named export quanto via window
const CallixDialer = {
  CallixDialerWidget,
  init,
  version: WIDGET_VERSION,
};

// Named exports para ES modules
export { CallixDialerWidget, init };

// Default export para compatibilidade
export default CallixDialer;

// Tornar disponível globalmente quando carregado via <script>
// Isso garante que window.CallixDialer.init() funcione no browser
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).CallixDialer = CallixDialer;
}
